import React, { useEffect, useState } from 'react';
import {
  Button,
  Select,
  Skeleton,
  TextArea,
  TextField,
  Title
} from '@gerdau/hefesto';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { Button as ButtonAntd } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FabricationContractsSelect from '../../../../components/FabricationContractsSelect';
import IsManagerFabricationContractInput from '../../../../components/IsManagerFabricationContractInput';
import { ERoutes } from '../../../../routes';
import { FabricationOrdersService } from '../../../../services/FabricationOrders';
import { ApplicationState } from '../../../../store';
import {
  loadCustomerContracts,
  updateConstructionRegistrationSettings
} from '../../../../store/ducks/contracts-and-constructions/actions';
import {
  ETowerOption,
  INITIAL_CONSTRUCTION_REGISTRATION
} from '../../../../store/ducks/contracts-and-constructions/types';
import {
  loadContractByContractNumberV2,
  loadContractDetails,
  loadContractDetailsV2
} from '../../../../store/ducks/fabrication-contracts/actions';
import {
  EFabricationContractsStatus,
  IFabricationContract
} from '../../../../store/ducks/fabrication-contracts/types';
import { IFabricationOrder } from '../../../../store/ducks/fabrication-orders/types';
import {
  EFabricationOrderSteps,
  IConstructionIdentificationState,
  IFabricationPurchaseOrderState
} from '../../../../store/ducks/fabrication-purchase-order/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ECustomerTypeView } from '../../../../store/ducks/user/types';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { cepInputValidator } from '../../../../utils/cepInputValidator';
import {
  textEllipsis,
  uppercaseOnlyFirstLetter
} from '../../../../utils/formatStrings';
import { getFabricationPurchaseStateFromFabricationOrder } from '../../../../utils/purchaseHelpers';
import { EConstructionsRoutes } from '../../../ContractsAndConstructions/contractsAndConstructions.routes';
import { EFabricationRoutes } from '../fabrication.routes';
import { isAllFieldsValidInConstructionIdentification } from '../fabricationOrderFieldsValidations';
import { FabricationContainer, FabricationErrorBlock } from '../styles';
import ContractWithoutConstructions from './ContractWithoutConstructions';
import LoadingFabricationOrderModal from './LoadingFabricationOrderModal';
import {
  ButtonActionContainer,
  ButtonContainer,
  ConstructionContainer,
  ConstructionIdentificationContentBlock,
  ContructionIdentificationInputContainer,
  DeliveryNotesContainer,
  OrderContainer,
  SearchContractButtonContainer
} from './styles';

interface IConstructionIdentificationProps {
  constructionIdentificationState: IConstructionIdentificationState;
  setConstructionIdentificationState(
    constructionIdentificationState: IConstructionIdentificationState
  ): void;
  onChangeContractNumber(): void;
  onInitFabricationRoute(fabricationOrderStep: EFabricationOrderSteps): void;
  goToFabricationRoute(route: EFabricationRoutes): void;
  onBuyAgain(fabricationPurchaseState: IFabricationPurchaseOrderState): void;
}

const ConstructionIdentification: React.FC<
  IConstructionIdentificationProps
> = ({
  constructionIdentificationState,
  setConstructionIdentificationState,
  goToFabricationRoute,
  onInitFabricationRoute,
  onChangeContractNumber,
  onBuyAgain
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const contractsState = useSelector(
    (appState: ApplicationState) => appState.fabricationContracts
  );
  const userState = useSelector((appState: ApplicationState) => appState.user);

  const contractAndConstructionsContractsManager = useSelector(
    (state: ApplicationState) =>
      state.contractsAndConstructions.contractsManager
  );

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    history.location.pathname,
    true
  );
  const getSelectedContract = (contractNumber?: string) => {
    return contractsState.contracts?.find(
      (contr) => !!contractNumber && contr.contractNumber === contractNumber
    );
  };

  const [isManagercontractNumber, setIsManagerContractNumber] = useState(
    constructionIdentificationState.contractNumber
  );

  const [selectedContract, setSelectedContract] = useState(
    getSelectedContract(constructionIdentificationState.contractNumber)
  );

  useEffect(() => {
    dispatch(
      sendTagging({ screenName, page: location.pathname, event: 'page_view' })
    );
  }, []);

  useEffect(() => {
    onInitFabricationRoute(EFabricationOrderSteps.CONSTRUCTION_IDENTIFICATION);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUpdateStateField = (field: string, newValue: any) => {
    setConstructionIdentificationState({
      ...constructionIdentificationState,
      [field]: newValue
    });
  };

  const msgHelperText =
    'O código do pedido deve ter no mínimo 3 caracteres, no máximo 24 caracteres e não pode conter caracteres especiais (permitido "-")';

  const onNextStepClick = () => {
    goToFabricationRoute(EFabricationRoutes.DETAILING_PROJECT);
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:CorteEDobra:IdentificacaoObra',
        event: 'click',
        itemName: 'button:avancarParaDetalhamento',
        itemDescription:
          'comprar_corteEDobra_identificacaoObra_avancarParaDetalhamento',
        filterInfo: `#cttObra:${selectedContract?.contractNumber}`,
        contratoObra: selectedContract?.contractNumber
      })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onClickCancelButton = () => {
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:CorteEDobra:IdentificacaoObra',
        itemName: 'button:cancelar',
        event: 'click',
        filterInfo: `#cttObra:${selectedContract?.contractNumber}`,
        contratoObra: selectedContract?.contractNumber,
        itemDescription: `comprar_corteEDobra_identificacaoObra_cancelar`
      })
    );
    history.push(`${ERoutes.PURCHASE}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeContractNumber = (selectedContractNumber: string) => {
    if (contractsState.contracts === undefined) {
      return;
    }

    const contract = getSelectedContract(
      selectedContractNumber
    ) as IFabricationContract;

    setConstructionIdentificationState({
      ...constructionIdentificationState,
      contractNumber: contract?.contractNumber,
      location: contract?.location,
      address: '',
      state: '',
      city: '',
      cep: '',
      towers: []
    });

    onChangeContractNumber();
    !!contract.contractNumber &&
      dispatch(loadContractByContractNumberV2(contract.contractNumber));
    setSelectedContract(contract);
  };

  const handleLoadContractByContractNumber = () => {
    if (!isManagercontractNumber) {
      return;
    }

    setConstructionIdentificationState({
      ...constructionIdentificationState,
      contractNumber: isManagercontractNumber,
      location: '',
      address: '',
      state: '',
      city: '',
      cep: '',
      towers: []
    });

    onChangeContractNumber();
    dispatch(loadContractByContractNumberV2(isManagercontractNumber));
  };

  const handleLoadContractDetails = (
    selectedContractNumber: string,
    customerShipTo?: string
  ) => {
    dispatch(loadContractDetails(selectedContractNumber, customerShipTo));
  };

  const handleLoadContractDetailsV2 = (selectedContractNumber: string) => {
    dispatch(loadContractDetailsV2(selectedContractNumber));
  };

  const initConstructionIdentificationAddress = () => {
    if (
      constructionIdentificationState.contractNumber &&
      contractsState.contracts
    ) {
      const contract = getSelectedContract(
        constructionIdentificationState.contractNumber
      );

      if (
        contract &&
        contract.shipTo &&
        contract.shipTo.address &&
        (!constructionIdentificationState.cep ||
          constructionIdentificationState.cep !== contract.shipTo.zipCode)
      ) {
        setConstructionIdentificationState({
          ...constructionIdentificationState,
          location: contract.location,
          address: contract.shipTo.address,
          state: contract.shipTo.state || '',
          city: contract.shipTo.city || '',
          cep: contract.shipTo.zipCode || ''
        });
      } else if (
        !userState.isManager &&
        contract &&
        contract.status === EFabricationContractsStatus.OPEN &&
        !contract.shipTo?.address &&
        !contract.loadingDetails &&
        !contract.errorDetails
      ) {
        handleLoadContractDetailsV2(
          constructionIdentificationState.contractNumber
        );
      }
      setSelectedContract(contract);
    }
  };

  useEffect(() => {
    initConstructionIdentificationAddress();
  });

  const canShowToNextStep = () => {
    return isAllFieldsValidInConstructionIdentification(
      constructionIdentificationState,
      contractsState
    );
  };

  const isAnewContractSelected = (): boolean => {
    return (
      !!selectedContract &&
      selectedContract.status === EFabricationContractsStatus.NEW_CONTRACT
    );
  };

  const hasErrorOnLoadSelectedContractDetails = (): boolean => {
    return !!selectedContract && !!selectedContract.errorDetails;
  };

  const cleanConstructionIdentificationState = () => {
    setSelectedContract(undefined);
    setConstructionIdentificationState({
      ...constructionIdentificationState,
      contractNumber: undefined,
      location: '',
      address: '',
      state: '',
      city: '',
      cep: ''
    });
  };

  const goToConstructionRegistration = () => {
    if (selectedContract) {
      if (
        selectedContract.soldTo.code !==
          contractAndConstructionsContractsManager.selectedCustomerId ||
        contractAndConstructionsContractsManager.error
      ) {
        const customerType = userState.searchCustomerType;
        const customerId =
          customerType === ECustomerTypeView.EMITTER
            ? selectedContract.soldTo.code
            : selectedContract.shipTo.code;

        dispatch(loadCustomerContracts(customerId, customerType));
      }

      dispatch(
        updateConstructionRegistrationSettings({
          ...INITIAL_CONSTRUCTION_REGISTRATION,
          contractNumber: selectedContract.contractNumber
        })
      );

      history.push(
        `${ERoutes.CONTRACTS_AND_CONSTRUCTIONS}${EConstructionsRoutes.CONSTRUCTION_REGISTRATION}`
      );
    }
  };

  const [showLoadingOrderModal, setShowLoadingOrderModal] = useState(false);
  const [loadFabricationOrderState, setLoadFabricationOrderState] = useState<{
    loading: boolean;
    error: boolean;
    orderId?: number;
    orderNumber?: string;
  }>({ loading: false, error: false });

  const onBuyAgainClick = async (orderId: number) => {
    const orderNumber =
      selectedContract?.lastOrderDetails?.find((lod) => lod.id === orderId)
        ?.orderNumber || '';

    const loadFabricationOrderFromId = async (): Promise<IFabricationOrder> => {
      const fabricationOrdersService = new FabricationOrdersService();
      return fabricationOrdersService.getFabricationOrderFromId(orderId);
    };

    try {
      setLoadFabricationOrderState({
        loading: true,
        error: false,
        orderId,
        orderNumber
      });
      setShowLoadingOrderModal(true);
      const fabricationOrder = await loadFabricationOrderFromId();
      const fabricationPurchaseState =
        getFabricationPurchaseStateFromFabricationOrder(fabricationOrder);
      onBuyAgain(fabricationPurchaseState);
      setLoadFabricationOrderState({
        loading: false,
        error: false,
        orderId,
        orderNumber
      });
    } catch (error) {
      setLoadFabricationOrderState({
        loading: false,
        error: true,
        orderId,
        orderNumber
      });
    }
  };

  const getCustomerOrderText = (): string | undefined => {
    if (
      !isManagercontractNumber ||
      isManagercontractNumber.length < 6 ||
      !contractsState.contracts ||
      contractsState.contracts.length === 0
    ) {
      return undefined;
    } else {
      const contract = contractsState.contracts?.find(
        (cont) => cont.contractNumber === isManagercontractNumber
      );
      return (
        contract &&
        (`${contract.contractNumber} - ${textEllipsis(
          contract.customerOrder,
          24
        )}` ||
          undefined)
      );
    }
  };

  const canShowTowersSelect = (): boolean => {
    return (
      !!selectedContract &&
      selectedContract.logistic?.towerOption !== null &&
      selectedContract.logistic?.towerOption !== ETowerOption.UNIQUE &&
      !!selectedContract.logistic?.towers &&
      selectedContract.logistic?.towers.length > 0
    );
  };

  const orderNumberAlreadyExistsForSelectedContract =
    selectedContract?.lastOrderDetails?.some(
      (order) =>
        order.orderNumber === constructionIdentificationState.orderNumber
    ) || false;

  const onChangeOrderNumber = (value: string) => {
    const regex = /[^a-zA-Z0-9--]+$/;
    const formattedValue = value.replace(regex, '');
    handleUpdateStateField('orderNumber', formattedValue.trim());
  };

  return (
    <>
      {isAnewContractSelected() && (
        <ContractWithoutConstructions
          cleanConstructionIdentificationState={
            cleanConstructionIdentificationState
          }
          goToConstructionRegistration={goToConstructionRegistration}
        />
      )}

      <FabricationContainer isFacelift={true}>
        <ConstructionContainer>
          <Title level={2}>Identificação da obra</Title>

          <span className="subtitle">
            Selecione um contrato para carregar as informações da sua obra.
          </span>

          <ConstructionIdentificationContentBlock className="last-order">
            {userState.isManager && (
              <ContructionIdentificationInputContainer className="facelift">
                <IsManagerFabricationContractInput
                  title="Contrato"
                  loading={contractsState.loading}
                  contractError={contractsState.contractError || false}
                  contractNumber={isManagercontractNumber || ''}
                  setContractNumber={setIsManagerContractNumber}
                  customerOrder={getCustomerOrderText()}
                />
              </ContructionIdentificationInputContainer>
            )}

            {!userState.isManager && (
              <ContructionIdentificationInputContainer className="facelift">
                <FabricationContractsSelect
                  contractNumber={
                    constructionIdentificationState.contractNumber
                  }
                  onChange={(value: string) =>
                    handleChangeContractNumber(value)
                  }
                  contracts={
                    (contractsState.contracts &&
                      contractsState.contracts.filter(
                        (contract) =>
                          contract.status ===
                            EFabricationContractsStatus.OPEN ||
                          contract.status ===
                            EFabricationContractsStatus.NEW_CONTRACT
                      )) ||
                    []
                  }
                  loading={contractsState.loading}
                />
              </ContructionIdentificationInputContainer>
            )}

            <ContructionIdentificationInputContainer className="last-order-facelift">
              <OrderContainer>
                <TextField
                  name="lastOrderNumberInput"
                  label="Último pedido:"
                  value={selectedContract?.lastOrderNumber || ' - '}
                  disabled={contractsState.loading}
                  readOnly
                ></TextField>
              </OrderContainer>
            </ContructionIdentificationInputContainer>

            <ButtonActionContainer>
              <Button
                name="lastOrdersButton"
                variant="secondary"
                disabled={
                  loadFabricationOrderState.loading ||
                  !selectedContract?.lastOrderNumber ||
                  !selectedContract?.lastOrderDetails
                }
                onClick={() => {
                  dispatch(
                    sendTagging({
                      screenName,
                      itemName: 'button:ver_ultimos_pedidos',
                      event: 'click',
                      filterInfo: `#cttObra:${selectedContract?.contractNumber}`,
                      contratoObra: selectedContract?.contractNumber,
                      itemDescription: `${itemDescription}_verUltimosPedidos`
                    })
                  );

                  goToFabricationRoute(EFabricationRoutes.LAST_ORDERS);
                }}
              >
                Ver últimos pedidos
              </Button>

              <Button
                name="repurchaseButton"
                variant="secondary"
                loading={loadFabricationOrderState.loading}
                disabled={
                  loadFabricationOrderState.loading ||
                  !selectedContract?.lastOrderNumber ||
                  !selectedContract?.lastOrderDetails
                }
                onClick={() => {
                  dispatch(
                    sendTagging({
                      screenName,
                      itemName: 'button:recomprar_ultimo_pedido',
                      event: 'click',
                      filterInfo: `#cttObra:${selectedContract?.contractNumber}`,
                      contratoObra: selectedContract?.contractNumber,
                      itemDescription: `${itemDescription}_recomprarUltimoPedido`
                    })
                  );

                  selectedContract &&
                    selectedContract.lastOrderDetails &&
                    onBuyAgainClick(selectedContract.lastOrderDetails[0].id);
                }}
              >
                Recomprar último pedido
              </Button>
            </ButtonActionContainer>

            {showLoadingOrderModal && loadFabricationOrderState.orderNumber && (
              <LoadingFabricationOrderModal
                loading={loadFabricationOrderState.loading}
                error={loadFabricationOrderState.error}
                orderNumber={loadFabricationOrderState.orderNumber}
                onClose={() => setShowLoadingOrderModal(false)}
              />
            )}
          </ConstructionIdentificationContentBlock>

          {userState.isManager && (
            <SearchContractButtonContainer>
              <Button
                variant="primary"
                name="searchContractButton"
                onClick={handleLoadContractByContractNumber}
                loading={contractsState.loading}
                disabled={
                  contractsState.loading ||
                  !isManagercontractNumber ||
                  isManagercontractNumber.length < 4
                }
              >
                Buscar
              </Button>
            </SearchContractButtonContainer>
          )}

          {constructionIdentificationState.contractNumber !== undefined &&
            hasErrorOnLoadSelectedContractDetails() && (
              <FabricationErrorBlock>
                <span>
                  Falha ao carregar os detalhes do contrato{' '}
                  {constructionIdentificationState.contractNumber}.
                </span>
                <ButtonAntd
                  type="primary"
                  onClick={() =>
                    constructionIdentificationState.contractNumber &&
                    handleLoadContractDetails(
                      constructionIdentificationState.contractNumber
                    )
                  }
                >
                  Tentar novamente
                </ButtonAntd>
              </FabricationErrorBlock>
            )}
        </ConstructionContainer>

        <ConstructionContainer>
          <Title level={2}>Identificação do pedido</Title>
          <ConstructionIdentificationContentBlock>
            <ContructionIdentificationInputContainer
              className={
                orderNumberAlreadyExistsForSelectedContract
                  ? 'has-error'
                  : undefined
              }
            >
              <TextField
                data-testid="order-number-test-id"
                label="Crie um código para o pedido"
                name="input-codigo-pedido"
                placeholder="Crie um código para o pedido"
                value={constructionIdentificationState.orderNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onChangeOrderNumber(e.target.value);
                }}
                supportMessage={
                  !showLoadingOrderModal &&
                  orderNumberAlreadyExistsForSelectedContract &&
                  constructionIdentificationState.comingFromBuyAgain
                    ? 'Insira um novo código.'
                    : orderNumberAlreadyExistsForSelectedContract &&
                      !constructionIdentificationState.comingFromBuyAgain
                    ? 'Já existe um pedido com o código digitado.'
                    : '' ||
                      (constructionIdentificationState.orderNumber &&
                        constructionIdentificationState.orderNumber.trim()
                          .length < 3)
                    ? 'Deve ter no mínimo 3 caracteres'
                    : ''
                }
                variant={
                  !showLoadingOrderModal &&
                  orderNumberAlreadyExistsForSelectedContract
                    ? 'danger'
                    : undefined ||
                      (constructionIdentificationState.orderNumber &&
                        constructionIdentificationState.orderNumber.trim()
                          .length < 3)
                    ? 'danger'
                    : undefined
                }
                maxLength={24}
                helperText={msgHelperText}
              />
            </ContructionIdentificationInputContainer>

            <ContructionIdentificationInputContainer>
              <DateField
                name="dateField"
                label="Selecione a data desejada:"
                helperText="A data de recebimento do pedido está 
                  sujeita a alterações"
                selected={
                  constructionIdentificationState.desiredDate
                    ? moment(
                        constructionIdentificationState.desiredDate,
                        'YYYY-MM-DD'
                      ).toDate()
                    : undefined
                }
                data-testid="dateFieldIdentificacaoPedido"
                onChange={(value: DateType) => {
                  if (value) {
                    handleUpdateStateField('desiredDate', value);
                  }
                }}
                minDate={moment().add(1, 'day').toDate()}
              />
            </ContructionIdentificationInputContainer>
          </ConstructionIdentificationContentBlock>
        </ConstructionContainer>

        <ConstructionContainer>
          <Title level={2}>Endereço da obra</Title>

          <ConstructionIdentificationContentBlock>
            <ContructionIdentificationInputContainer>
              {contractsState.loading ? (
                <Skeleton type="input" />
              ) : (
                <TextField
                  label={'CEP da obra'}
                  name={'cep'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const cep = cepInputValidator(e);
                    handleUpdateStateField('cep', cep);
                  }}
                  value={constructionIdentificationState.cep}
                  disabled={true}
                />
              )}
            </ContructionIdentificationInputContainer>
          </ConstructionIdentificationContentBlock>

          <ConstructionIdentificationContentBlock>
            <ContructionIdentificationInputContainer className="double-width">
              {contractsState.loading ? (
                <Skeleton type="input" />
              ) : (
                <TextField
                  value={uppercaseOnlyFirstLetter(
                    constructionIdentificationState.address
                  )}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleUpdateStateField('address', e.target.value)
                  }
                  disabled={true}
                  label={'Logradouro'}
                  name={'logradouro'}
                />
              )}
            </ContructionIdentificationInputContainer>
          </ConstructionIdentificationContentBlock>

          <ConstructionIdentificationContentBlock>
            <ContructionIdentificationInputContainer>
              {canShowTowersSelect() ? (
                <Select
                  value={constructionIdentificationState.towers}
                  onSelect={(value) =>
                    handleUpdateStateField('towers', value as string[])
                  }
                  disabled={!selectedContract}
                  options={
                    selectedContract?.logistic?.towers.map((tower) => {
                      return {
                        label: tower,
                        value: tower
                      };
                    }) as TSelectOptionProps[]
                  }
                  isMultipleSelect={true}
                  label={'Selecione a torre'}
                  name={'towers'}
                  helperText={
                    'Torre ou bloco se refere ao setor do edifício para o qual o contrato se refere.'
                  }
                ></Select>
              ) : (
                <TextField
                  value={
                    constructionIdentificationState.towers &&
                    constructionIdentificationState.towers.length > 0
                      ? constructionIdentificationState.towers[0]
                      : ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleUpdateStateField('towers', [e.target.value]);
                  }}
                  label={'Torre ou Bloco'}
                  name={'towerOrBlock'}
                  helperText={
                    'Torre ou bloco se refere ao setor do edifício para o qual o contrato se refere.'
                  }
                />
              )}
            </ContructionIdentificationInputContainer>
          </ConstructionIdentificationContentBlock>

          <ConstructionIdentificationContentBlock>
            <ContructionIdentificationInputContainer>
              {contractsState.loading ? (
                <Skeleton type="input" />
              ) : (
                <Select
                  value={constructionIdentificationState.state}
                  onSelect={(value) =>
                    handleUpdateStateField('state', value as string)
                  }
                  disabled={true}
                  label={'UF'}
                  name={'UF'}
                  options={[
                    {
                      label: constructionIdentificationState.state,
                      value: constructionIdentificationState.state
                    }
                  ]}
                ></Select>
              )}
            </ContructionIdentificationInputContainer>

            <ContructionIdentificationInputContainer>
              {contractsState.loading ? (
                <Skeleton type="input" />
              ) : (
                <Select
                  value={uppercaseOnlyFirstLetter(
                    constructionIdentificationState.city
                  )}
                  onSelect={(value) =>
                    handleUpdateStateField('city', value as string)
                  }
                  disabled={true}
                  label={'Selecione a cidade'}
                  name={'city'}
                  options={[
                    {
                      label: uppercaseOnlyFirstLetter(
                        constructionIdentificationState.city
                      ),
                      value: uppercaseOnlyFirstLetter(
                        constructionIdentificationState.city
                      )
                    }
                  ]}
                ></Select>
              )}
            </ContructionIdentificationInputContainer>
          </ConstructionIdentificationContentBlock>
        </ConstructionContainer>

        <ConstructionContainer>
          <DeliveryNotesContainer>
            <Title level={2}>Observações de entrega</Title>

            <TextArea
              name="deliveryNotesTextArea"
              label=""
              value={constructionIdentificationState.observation}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleUpdateStateField('observation', e.target.value)
              }
              cols={200}
              resizable={false}
              maxLength={500}
              supportMessage={
                constructionIdentificationState.observation.length == 500
                  ? 'Deve ter no máximo 500 caracteres'
                  : ''
              }
            />
          </DeliveryNotesContainer>
        </ConstructionContainer>
        <ConstructionContainer>
          <ButtonContainer>
            <Button
              type="button"
              variant="secondary"
              labelPosition="center"
              name="cancelAndReturnButton"
              onClick={onClickCancelButton}
            >
              Cancelar e voltar
            </Button>
            <Button
              name="goToDetailButton"
              onClick={onNextStepClick}
              variant="primary"
              disabled={!canShowToNextStep()}
            >
              Avançar para detalhamento
            </Button>
          </ButtonContainer>
        </ConstructionContainer>
      </FabricationContainer>
    </>
  );
};

export default ConstructionIdentification;
